import React, { useMemo } from 'react';
import { ListPage } from '../../../components/crud/ListPage';
import { DataTable } from '../../../components/ui/data-table';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { useTable } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, Eye, Edit, AlertTriangle, Wrench, CheckCircle, XCircle, Clock, Activity, WifiOff, HelpCircle } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';

const statusMap: Record<string, string> = { active: 'success', inactive: 'default', maintenance: 'warning', error: 'error', offline: 'error' };

// Backend owns the verdict (AnalyzerDeviceSerializer.get_connectivity); this maps
// it to a look. Anything unrecognised falls through to `unknown` rather than
// rendering blank — an unstyled state must not read as "fine".
const linkLook: Record<string, { variant: string; icon: any }> = {
  sending: { variant: 'success', icon: Activity },
  idle: { variant: 'info', icon: Clock },
  connected_no_data: { variant: 'warning', icon: AlertTriangle },
  no_contact: { variant: 'default', icon: WifiOff },
  listener_down: { variant: 'error', icon: XCircle },
  connector_offline: { variant: 'error', icon: WifiOff },
  unknown: { variant: 'default', icon: HelpCircle },
};
const categoryColors: Record<string, string> = { chemistry: 'info', hematology: 'error', immunology: 'processing', microbiology: 'success', molecular: 'warning', urinalysis: 'info', coagulation: 'processing', blood_gas: 'info', other: 'default' };

export const AnalyzerDevicesList: React.FC = () => {
  const navigate = useNavigate();
  // Connectivity is live state, so the page refreshes itself — during
  // commissioning someone is watching this screen while another person walks
  // between the instruments, and a stale table sends them the wrong way.
  const { tableQueryResult: { data: tableData, isLoading } } = useTable({
    resource: 'devices/analyzers',
    syncWithLocation: false,
    queryOptions: { refetchInterval: 30000 },
  });
  const devices = useMemo(() => (tableData?.data || []) as any[], [tableData]);

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'name', header: 'Analyzer',
      cell: ({ row }) => (
        <div>
          <div className="flex items-center gap-2"><FlaskConical className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{row.original.name}</span></div>
          <div className="text-xs text-muted-foreground">{row.original.manufacturer} {row.original.model}</div>
          <div className="text-[11px] text-muted-foreground">S/N: {row.original.serial_number}</div>
        </div>
      ),
    },
    {
      id: 'category', header: 'Category & Location',
      cell: ({ row }) => (
        <div>
          <Badge variant={(categoryColors[row.original.category] || 'default') as any} className="mb-1">{row.original.category_display}</Badge>
          <div className="text-sm">{row.original.branch_name}</div>
          <div className="text-xs text-muted-foreground">via {row.original.connector_name}</div>
        </div>
      ),
    },
    {
      id: 'connection', header: 'Connection',
      cell: ({ row }) => (
        <div>
          <div className="text-sm">{row.original.connection_type_display}</div>
          <code className="text-[10px] bg-muted px-1 rounded">{row.original.device_ip}:{row.original.device_port}</code>
          <div><Badge variant="info" className="text-[10px] mt-0.5">{row.original.protocol_display}</Badge></div>
        </div>
      ),
    },
    {
      accessorKey: 'status', header: 'Status',
      cell: ({ row }) => (
        <div>
          <Badge variant={(statusMap[row.original.status] || 'default') as any}>{row.original.status_display}</Badge>
          {row.original.is_due_for_maintenance && <div className="text-[10px] text-warning-700 mt-1 flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" />Maintenance Due</div>}
          {row.original.is_due_for_calibration && <div className="text-[10px] text-destructive-700 flex items-center gap-0.5"><Wrench className="h-3 w-3" />Calibration Due</div>}
        </div>
      ),
    },
    {
      id: 'link', header: 'Connectivity',
      cell: ({ row }) => {
        const c = row.original.connectivity;
        if (!c) return <span className="text-xs text-muted-foreground">—</span>;
        const look = linkLook[c.state] || linkLook.unknown;
        const Icon = look.icon;
        return (
          <div className="min-w-[150px]">
            <Badge variant={look.variant as any} className="flex w-fit items-center gap-1">
              <Icon className="h-3 w-3" />{c.label}
            </Badge>
            {c.detail && <div className="text-[11px] text-muted-foreground mt-0.5">{c.detail}</div>}
            {c.peer && (c.state === 'sending' || c.state === 'idle' || c.state === 'connected_no_data') && (
              <div className="text-[10px] text-muted-foreground">from {c.peer}</div>
            )}
            {c.state === 'no_contact' && c.listen_target && (
              <div className="text-[10px] text-muted-foreground">waiting on {c.listen_target}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'qc', header: 'Quality Control',
      cell: ({ row }) => (
        <div>
          <div className="flex items-center gap-1 text-xs mb-1">
            {row.original.qc_enabled ? <><CheckCircle className="h-3 w-3 text-success-700" /> Enabled</> : <><XCircle className="h-3 w-3 text-destructive-700" /> Disabled</>}
          </div>
          {row.original.qc_enabled && (
            <div className="text-[11px] text-muted-foreground">
              <div>Every {row.original.qc_frequency_hours}h</div>
              <div>Last: {row.original.last_qc_run ? new Date(row.original.last_qc_run).toLocaleDateString() : 'Never'}</div>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'performance', header: 'Performance',
      cell: ({ row }) => (
        <div className="text-xs space-y-0.5">
          <div>Tests: {(row.original.total_tests_processed ?? 0).toLocaleString()}</div>
          <div className={row.original.error_rate_percentage > 5 ? 'text-destructive-700' : 'text-success-700'}>Error: {row.original.error_rate_percentage}%</div>
          <div>Avg: {row.original.avg_test_time_seconds}s</div>
        </div>
      ),
    },
    {
      id: 'activity', header: 'Last Activity',
      cell: ({ row }) => (
        <div className="text-xs">
          <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{row.original.last_data_received ? new Date(row.original.last_data_received).toLocaleDateString() : 'No data'}</div>
          <div className="text-muted-foreground">Tests: {row.original.supported_tests?.length || 0} types</div>
        </div>
      ),
    },
    {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/devices/analyzers/show/${row.original.id}`)}><Eye className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => navigate(`/devices/analyzers/edit/${row.original.id}`)}><Edit className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <ListPage title="Analyzer Devices" createButtonProps={{ onClick: () => navigate('/devices/analyzers/create'), children: 'Add Analyzer' }}>
      <DataTable columns={columns} data={devices} isLoading={isLoading} searchKey="name" emptyMessage="No analyzer devices found" onRowClick={(row) => navigate(`/devices/analyzers/edit/${row.id}`)} />
    </ListPage>
  );
};

export default AnalyzerDevicesList;
